import BassService, { getBarLength, getNotesLengthWhenPlayed, PlayNotesOptions } from "../../services/bass-service";
import { preset as electricFingerPreset, presetName as electricFingerPresetName } from '../../soundfonts/electric_bass_finger';
import { preset as electricPickPreset, presetName as electricPickPresetName } from '../../soundfonts/electric_bass_pick';
import { preset as acousticPreset, presetName as acousticPresetName } from '../../soundfonts/acoustic_bass';
import { preset as acousticPianoPreset, presetName as acousticPianoPresetName } from '../../soundfonts/acoustic_grand_piano';
import getSong from '../../songs/should-i-stay-or-should-i-go';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMusic, faPlay, faStop } from "@fortawesome/free-solid-svg-icons";
import { fretBoard, Note, noteValues, strings } from "../../constants/bass";
import { useEffect, useMemo, useReducer } from "react";

export interface EarTrainingGameProps {
    puzzle?: PlayNotesOptions
    onComplete?: () => void;
}

interface EarTrainingGameNote {
    index: number;
    note: Note;
    answer: string;
    isPlaying: boolean;
}

interface EarTrainingGameModel {
    isPlaying: boolean;
    notes: Array<EarTrainingGameNote>;
    gameState: GameState;
    soundFontIndex: number;
}

enum GameState {
    Start = 1,
    Incorrect = 2,
    Correct = 3
}

const presets = [
    { preset: electricFingerPreset, presetName: electricFingerPresetName },
    { preset: electricPickPreset, presetName: electricPickPresetName },
    { preset: acousticPreset, presetName: acousticPresetName },
    { preset: acousticPianoPreset, presetName: acousticPianoPresetName },
];

export default function EarTrainingGame({ puzzle, onComplete }: EarTrainingGameProps) {

    const initialState: EarTrainingGameModel = {
        isPlaying: false,
        gameState: GameState.Start,
        notes: [],
        soundFontIndex: 0
    };
    if (puzzle?.notes) {
        initialState.notes = puzzle.notes.map((note, i) => {
            return {
                index: i,
                note: note,
                answer: 'A',
                isPlaying: false
            };
        })
    }

    const [state, dispatch] = useReducer(reducer, initialState);
    const soundFontIndex = state.soundFontIndex ?? 0;
    const bass = useMemo(() => new BassService(), []);

    useEffect(() => {
        return () => {
            dispose();
        };
    }, []);

    useEffect(() => {
        // do nothing
    }, [state.gameState, state.isPlaying]);

    function reducer(state: EarTrainingGameModel, action: any): EarTrainingGameModel {

        switch (action.type) {
            case 'ATTEMPT':
                return {
                    ...state, gameState: GameState.Start
                };
            case 'WIN':
                return {
                    ...state, gameState: GameState.Correct
                };
            case 'WRONG':
                return {
                    ...state, gameState: GameState.Incorrect
                };
            case 'STOP_PLAYING':
                return {
                    ...state, isPlaying: false
                }
            case 'START_PLAYING':
                return {
                    ...state, isPlaying: true
            }
            case 'ANSWER_SELECTED':

                const newNotes = state.notes.map(note => {

                    if (note.index === action.noteIndex) {
                        return {
                            ...note,
                            answer: action.answer
                        } as EarTrainingGameNote;
                    }
                    else {
                        return { ...note } as EarTrainingGameNote;
                    }

                });

                return {
                    ...state, notes: newNotes
                };
            case 'SELECT_SOUNDFONT':
                return {
                    ...state, soundFontIndex: action.soundFontIndex
                };
        }

        return {
            ...state
        };
    }

    function play() {

        if (puzzle) {
            puzzle.repeat = true;
            bass.playNotes(puzzle, presets[soundFontIndex].preset, presets[soundFontIndex].presetName);
            dispatch({ type: 'START_PLAYING' });
        }
    };

    function stop() {
        bass.stop();
        dispatch({ type: 'STOP_PLAYING' });
    }

    function onAnswerSelected(event: React.FormEvent<HTMLSelectElement>) {
        const index = parseInt(event.currentTarget.getAttribute('data-index') ?? '');
        const answer = event.currentTarget.value;

        if (!isNaN(index)) {
            dispatch({ type: 'ANSWER_SELECTED', noteIndex: index, answer: answer });
        }
    }

    function onSoundFontSelected(event: React.FormEvent<HTMLSelectElement>) {
        const index = parseInt(event.currentTarget.value);

        if (!isNaN(index)) {
            stop();
            dispatch({ type: 'SELECT_SOUNDFONT', soundFontIndex: index });
        }
    }

    function onSubmit() {
        stop();
        dispatch({ type: 'ATTEMPT' });

        // check answers
        let isCorrect = true;
        state.notes.forEach(note => {
            if (!noteValues[note.answer].includes(note.note.pitch ?? 0)) {
                isCorrect = false;
            }
        });

        if (isCorrect) {
            dispatch({ type: 'WIN' });
        }
        else {
            dispatch({ type: 'WRONG' });
        }
    }

    function dispose() {
        bass.dispose();
    }

    return (
        <>
            {puzzle &&
                <div className="ear-training-game">
                    <div className="d-flex">
                        <button className="btn btn-light me-2" onClick={play} disabled={state.isPlaying}><FontAwesomeIcon icon={faPlay} /></button>
                        <button className="btn btn-light" onClick={stop} disabled={!state.isPlaying}><FontAwesomeIcon icon={faStop} /></button>
                        <select className="form-select ms-3" value={state.soundFontIndex} onChange={onSoundFontSelected}>
                            <option value="0">Electric Bass - Finger</option>
                            <option value="1">Electric Bass - Pick</option>
                            <option value="2">Acoustic Bass</option>
                            <option value="3">Acoustic Grand Piano</option>
                        </select>
                    </div>

                    <div className="d-flex justify-content-around mt-3">
                        {state.notes.map(note =>
                            <div key={`note-${note.index}`} className="card" >
                                <div className="thumbnail"><FontAwesomeIcon icon={faMusic} /></div>
                                <div className="card-body">
                                    {/* <p className="card-text">{note.note.pitch} - {note.note.duration}</p> */}
                                    <select data-index={note.index} className="form-select" value={note.answer} onChange={onAnswerSelected}>
                                        <option value="A">A</option>
                                        <option value="Asharp">A♯ / B♭</option>
                                        <option value="B">B</option>
                                        <option value="C">C</option>
                                        <option value="Csharp">C♯ / D♭</option>
                                        <option value="D">D</option>
                                        <option value="Dsharp">D♯ / E♭</option>
                                        <option value="E">E</option>
                                        <option value="F">F</option>
                                        <option value="Fsharp">F♯ / G♭</option>
                                        <option value="G">G</option>
                                        <option value="Gsharp">G♯ / A♭</option>
                                        <option value="E">E - open</option>
                                        <option value="F">E - 1</option>
                                        <option value="Fsharp">E - 2</option>
                                        <option value="G">E - 3</option>
                                        <option value="Gsharp">E - 4</option>
                                        <option value="A">E - 5</option>
                                        <option value="Asharp">E - 6</option>
                                        <option value="B">E - 7</option>
                                        <option value="C">E - 8</option>
                                        <option value="Csharp">E - 9</option>
                                        <option value="D">E - 10</option>
                                        <option value="Dsharp">E - 11</option>
                                        <option value="E">E - 12</option>
                                        <option value="A">A - open</option>
                                        <option value="Asharp">A - 1</option>
                                        <option value="B">A - 2</option>
                                        <option value="C">A - 3</option>
                                        <option value="Csharp">A - 4</option>
                                        <option value="D">A - 5</option>
                                        <option value="Dsharp">A - 6</option>
                                        <option value="E">A - 7</option>
                                        <option value="F">A - 8</option>
                                        <option value="Fsharp">A - 9</option>
                                        <option value="G">A - 10</option>
                                        <option value="Gsharp">A - 11</option>
                                        <option value="A">A - 12</option>
                                        <option value="D">D - open</option>
                                        <option value="Dsharp">D - 1</option>
                                        <option value="E">D - 2</option>
                                        <option value="F">D - 3</option>
                                        <option value="Fsharp">D - 4</option>
                                        <option value="G">D - 5</option>
                                        <option value="Gsharp">D - 6</option>
                                        <option value="A">D - 7</option>
                                        <option value="Asharp">D - 8</option>
                                        <option value="B">D - 9</option>
                                        <option value="C">D - 10</option>
                                        <option value="Csharp">D - 11</option>
                                        <option value="D">D - 12</option>
                                        <option value="G">G - open</option>
                                        <option value="Gsharp">G - 1</option>
                                        <option value="A">G - 2</option>
                                        <option value="Asharp">G - 3</option>
                                        <option value="B">G - 4</option>
                                        <option value="C">G - 5</option>
                                        <option value="Csharp">G - 6</option>
                                        <option value="D">G - 7</option>
                                        <option value="Dsharp">G - 8</option>
                                        <option value="E">G - 9</option>
                                        <option value="F">G - 10</option>
                                        <option value="Fsharp">G - 11</option>
                                        <option value="G">G - 12</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="text-center mt-4">
                        <button className="btn btn-primary" onClick={onSubmit}>Submit</button>
                    </div>

                    {state.gameState === GameState.Incorrect &&
                        <div className="alert alert-danger mt-3 d-flex justify-content-between align-items-center">
                            <span>Incorrect. Try again.</span>
                            <button className="btn btn-link" onClick={onComplete}>Try another puzzle</button>
                        </div>
                    }

                    {state.gameState === GameState.Correct &&
                        <div className="alert alert-success mt-3 d-flex justify-content-between align-items-center">
                            <p>Correct!</p> <button className="btn btn-link" onClick={onComplete}>Try another puzzle</button>
                        </div>
                    }
                </div>
            }
            {!puzzle &&
                <div>No puzzle generated</div>
            }
        </>
    );
}